import lamejs from '@breezystack/lamejs';
import { convertToWav, decodeAudioData } from 'shared/helpers/audioConversion';

// O encoder WAV e a decodificação MORAM em `shared/helpers/audioConversion.js` desde que o
// widget passou a gravar nota de voz: eram o mesmo código nos dois lugares, e duas cópias de
// um encoder de PCM é uma a mais do que se consegue manter coerente. O que fica aqui é o MP3,
// que só o painel do atendente usa — é ele que arrasta o `lamejs`, e é por isso que ele não
// desce para o pacote do widget.
export { convertToWav };

export const encodeToMP3 = (channels, sampleRate, samples, bitrate = 128) => {
  const outputBuffer = [];
  const encoder = new lamejs.Mp3Encoder(channels, sampleRate, bitrate);
  const maxSamplesPerFrame = 1152;

  for (let offset = 0; offset < samples.length; offset += maxSamplesPerFrame) {
    const sliceEnd = Math.min(offset + maxSamplesPerFrame, samples.length);
    const sampleSlice = samples.subarray(offset, sliceEnd);
    const mp3Buffer = encoder.encodeBuffer(sampleSlice);

    if (mp3Buffer.length > 0) {
      outputBuffer.push(new Int8Array(mp3Buffer));
    }
  }

  const remainingData = encoder.flush();
  if (remainingData.length > 0) {
    outputBuffer.push(new Int8Array(remainingData));
  }

  return new Blob(outputBuffer, { type: 'audio/mp3' });
};

/**
 * Converts an audio Blob to an MP3 format Blob.
 * @param {Blob} audioBlob - The audio data as a Blob.
 * @param {number} bitrate - MP3 bitrate (default: 128)
 * @returns {Promise<Blob>} - A Blob containing the MP3 encoded audio.
 */
export const convertToMp3 = async (audioBlob, bitrate = 128) => {
  // eslint-disable-next-line no-console
  console.log('[audio-debug] convertToMp3:start', {
    blobType: audioBlob?.type,
    blobSize: audioBlob?.size,
    bitrate,
  });
  try {
    const audioBuffer = await decodeAudioData(audioBlob);
    // Downmix any number of channels to mono before encoding.
    // Voice notes are mono in practice, and lamejs's stereo encodeBuffer API
    // expects two separate Int16Array arguments (left, right). Forcing mono
    // sidesteps that path and works uniformly across browsers (Firefox often
    // decodes the MediaRecorder WebM/Opus blob as 2 channels).
    const channelCount = audioBuffer.numberOfChannels;
    const length = audioBuffer.length;
    const channelData = [];
    for (let c = 0; c < channelCount; c += 1) {
      channelData.push(audioBuffer.getChannelData(c));
    }
    const samples = new Int16Array(length);
    for (let i = 0; i < length; i += 1) {
      let sum = 0;
      for (let c = 0; c < channelCount; c += 1) {
        sum += channelData[c][i];
      }
      const mixed = Math.max(-1, Math.min(1, sum / channelCount));
      samples[i] = mixed < 0 ? mixed * 0x8000 : mixed * 0x7fff;
    }
    // eslint-disable-next-line no-console
    console.log('[audio-debug] convertToMp3:samplesReady', {
      samplesLength: samples.length,
      sourceChannels: channelCount,
      encodeChannels: 1,
      sampleRate: audioBuffer.sampleRate,
    });
    const mp3Blob = encodeToMP3(1, audioBuffer.sampleRate, samples, bitrate);
    // eslint-disable-next-line no-console
    console.log('[audio-debug] convertToMp3:done', {
      mp3Type: mp3Blob?.type,
      mp3Size: mp3Blob?.size,
    });
    return mp3Blob;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[audio-debug] convertToMp3:error', {
      name: error?.name,
      message: error?.message,
      code: error?.code,
      stack: error?.stack,
      original: error,
    });
    throw new Error('Conversion to MP3 failed.');
  }
};

export const convertAudio = async (inputBlob, outputFormat, bitrate = 128) => {
  // eslint-disable-next-line no-console
  console.log('[audio-debug] convertAudio:start', {
    outputFormat,
    bitrate,
    inputType: inputBlob?.type,
    inputSize: inputBlob?.size,
  });
  let audio;
  if (outputFormat === 'audio/wav') {
    audio = await convertToWav(inputBlob);
  } else if (outputFormat === 'audio/mp3') {
    audio = await convertToMp3(inputBlob, bitrate);
  } else {
    // eslint-disable-next-line no-console
    console.error('[audio-debug] convertAudio:unsupportedFormat', outputFormat);
    throw new Error('Unsupported output format');
  }
  // eslint-disable-next-line no-console
  console.log('[audio-debug] convertAudio:done', {
    outputType: audio?.type,
    outputSize: audio?.size,
  });
  return audio;
};
